"use client";

import { useCallback } from "react";
import { create } from "zustand";
import { useProjectConfigs } from "@/features/project/ProjectContext";

interface SidebarStore {
  /**
   * Null until the panel is actually moved.
   *
   * The project's own default stands in until then, rather than being written
   * here from an effect: an effect can only run after a paint, so the first
   * frame would show the panel in whatever state this was seeded with and the
   * real state would arrive as a change — which is a change the panel animates.
   */
  isOpen: boolean | null;
  setOpen: (isOpen: boolean) => void;
}

export const useSidebarStore = create<SidebarStore>((set) => ({
  isOpen: null,
  setOpen: (isOpen) => set({ isOpen }),
}));

/**
 * The result sidebar's open state.
 *
 * Two project settings shape it, and they mean different things:
 *  - `hideSidebar` is where the panel *starts*;
 *  - `sidebarCollapse: false` means the user may not change it at all, so open,
 *    close and toggle become no-ops rather than being hidden at the call site.
 *
 * `isAnimated` is false until the panel has been moved, and it gates the CSS
 * transition. Where the panel begins is not something to animate to.
 */
export function useSidebar() {
  const { hideSidebar, sidebarCollapse } = useProjectConfigs();
  const stored = useSidebarStore((state) => state.isOpen);
  const setOpen = useSidebarStore((state) => state.setOpen);

  const isCollapsible = sidebarCollapse !== false;
  const isOpen = stored ?? !hideSidebar;

  const open = useCallback(() => {
    if (isCollapsible) setOpen(true);
  }, [isCollapsible, setOpen]);

  const close = useCallback(() => {
    if (isCollapsible) setOpen(false);
  }, [isCollapsible, setOpen]);

  const toggle = useCallback(() => {
    if (isCollapsible) setOpen(!isOpen);
  }, [isCollapsible, isOpen, setOpen]);

  return { isOpen, open, close, toggle, isAnimated: stored !== null };
}
